import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  type MutableRefObject,
  type PropsWithChildren,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
  type SetStateAction,
  type Dispatch,
} from 'react';
import ReactSelect, { createFilter, type GroupBase } from 'react-select';

import { components } from './components/components.js';
import type { MenuListFunctionRefObject } from './components/MenuList.js';
import { SelectMenuContext } from './contexts/SelectMenuContext.js';
import { useSelectOptions, _isOption } from './hooks/use-select-options.js';
import { useSelectSlots } from './hooks/use-select-slots.js';
import { useSelectValue } from './hooks/use-select-value.js';
import { SelectContent } from './SelectContent.js';
import { SelectCustomTrigger } from './SelectCustomTrigger.js';
import { SelectDisplayValue } from './SelectDisplayValue.js';
import {
  SelectEmptyState,
  type SelectEmptyStateProps,
} from './SelectEmptyState.js';
import { SelectFilter, type SelectFilterProps } from './SelectFilter.js';
import { SelectGroup, SelectGroupLabel } from './SelectGroup.js';
import { SelectOption } from './SelectOption.js';
import { SelectPrefix, type SelectPrefixProps } from './SelectPrefix.js';
import { SelectSuffix } from './SelectSuffix.js';
import { SelectTrigger } from './SelectTrigger.js';
import {
  reducer,
  SelectActions,
  type SelectReducerAction,
} from './state/select-menu-state-reducer.js';
import * as styles from './styles/Select.sty.js';
import type {
  BaseOption,
  OptionOrGroup,
  SelectContentWidthOptionsType,
  SelectTriggerWidthOptionsType,
  SelectMultiValue,
  SelectSingleValue,
} from './types.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { createStratoLogger } from '../../core/sdk/sdk-logger.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import type {
  FormControlState,
  FormControlStateProps,
} from '../../core/types/form-control-state.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { withFormFieldMessageContextProviderWithGenerics } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type {
  FormControlRef,
  FormControlWithOverlayRef,
} from '../shared-types.js';

const COMPONENT_NAME = 'Select';

// Redeclare react-select module to extend it with additional props for typesafety.
// https://react-select.com/typescript#custom-select-props
declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<
    Option,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    IsMulti extends boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Group extends GroupBase<Option>,
  > {
    /** Custom placeholder text */
    placeholderText?: ReactNode;
    /** The trigger width set by the dev. */
    triggerWidth?: SelectTriggerWidthOptionsType;
    /** Custom select trigger */
    customTrigger?: ReactElement<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { [key: string]: any } | null | undefined
    >;
    /** Custom display value. */
    displayValue?: ReactNode[];
    /** Prefix for the select trigger */
    prefix?: ReactElement<SelectPrefixProps>;
    /* TriggerRef that needs to be applied to the trigger in order to setup the menu positioning */
    triggerRef: HTMLElement | null;
    /* setter for the TriggerRef */
    setTriggerRef: Dispatch<SetStateAction<HTMLDivElement | null>>;
    /** Defines if the space for the prefix icons should be reserved in all options rendered */
    optionsHavePrefix: boolean;
    /** Select.Filter component specified by dev */
    customFilter?: ReactElement<SelectFilterProps>;
    /** Custom empty state provided by dev */
    emptyState?: ReactElement<SelectEmptyStateProps>;
    /** Form control state for errors and hints */
    controlState?: FormControlState;
    loading?: boolean;
    clearable?: boolean;
    isRequired?: boolean;
    /** array of all select options (including those in groups) */
    allOptions?: BaseOption<Option>[];
    hiddenSelectRef: Ref<HTMLSelectElement>;
    hiddenSelectName?: string;
    /** whether the focus styles on the options should be visible */
    focusOnOption: boolean;
    /** Ref that needs to be applied to the filter input to manage the focus and keyboard navigation */
    filterRef?: MutableRefObject<FormControlRef<HTMLDivElement> | null>;
    /** Ref that needs to be applied to the clear selection element to manage the focus and keyboard navigation */
    clearSelectionRef?: RefObject<HTMLElement | null>;
    /** Ref that needs to be applied to the select content (menulist) element to manage the focus and keyboard navigation */
    contentRef?: RefObject<HTMLDivElement | null>;
    menuListFunctionRef?: RefObject<MenuListFunctionRefObject | null>;
    /** The content width set by the dev. */
    contentWidth?: SelectContentWidthOptionsType;
    /** Whether the selected options are reordered to the top of the options. */
    showSelectedOptionsFirst?: boolean;
    /** The id used for the hidden input. */
    selectInputId?: string;
    /** Labels the current element. */
    'aria-label'?: string;
    /** Identifies the element (or elements) that label(s) the current element. */
    'aria-labelledby'?: string;
    /** Identifies the element (or elements) that describe(s) the object. */
    'aria-describedby'?: string;
    /** Identifies the element (or elements) that provide(s) a detailed, extended description for the object. */
    'aria-details'?: string;
    /** className and style prop for each of the components. */
    componentProps: {
      trigger?: StylingProps &
        DataTestId &
        BehaviorTrackingProps & { readOnly: boolean; ariaDisabled?: boolean };
      content?: StylingProps & DataTestId & BehaviorTrackingProps;
      filter?: StylingProps & DataTestId & BehaviorTrackingProps;
      emptyState?: StylingProps & DataTestId & BehaviorTrackingProps;
    };
  }
}

// Redeclare react forwardRef to allow generic types on react
// https://fettblog.eu/typescript-react-generic-forward-refs/#option-3%3A-augment-forwardref
declare module 'react' {
  function forwardRef<T, P = unknown>(
    render: (
      props: P,
      ref: import('react').Ref<T>,
    ) => import('react').ReactElement | null,
  ): (
    props: P & import('react').RefAttributes<T>,
  ) => import('react').ReactElement | null;
}

/**
 * Common select base props.
 * @public
 */
export type SelectBaseProps<T, Multiple extends boolean> = PropsWithChildren<{
  /**
   * Used to compare options with objects for values either by a specified property of the object or through a comparator function.
   */
  compareBy?: keyof T | ((valueA: T, valueB: T) => boolean);

  /** Name used for the component when submitting it in a form */
  name?: string;

  /** Form name used to connect the component to a form */
  form?: string;

  /**
   * Boolean to determine whether the component is required
   *
   * @defaultValue false
   */
  required?: boolean;

  /**
   * Whether the component is disabled
   *
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Event handler called when the open state for the select changes
   */
  onOpenChange?: (isOpen: boolean) => void;

  /**
   * Whether or not the select allows multiple selection or not
   * @defaultValue false
   */
  multiple?: Multiple;

  /**
   * Whether or not the select is clearable.
   * @defaultValue false for single select, true for multi select
   */
  clearable?: boolean;

  /** Callback that is called when the select trigger loses focus. */
  onBlur?: (e: FocusEvent<HTMLInputElement, Element>) => void;

  /** Callback that is called when the select trigger gets focus. */
  onFocus?: (e: FocusEvent<HTMLInputElement, Element>) => void;
}> &
  MaskingProps;

/**
 * @public
 */
export type SelectOnChangeValue<
  OptionValue,
  Multiple extends boolean,
> = Multiple extends true
  ? SelectMultiValue<OptionValue>
  : SelectSingleValue<OptionValue>;

/**
 * Accepted properties for Select
 * @public
 */
export type SelectFormsProps<
  /*
   * TODO: Figure out how we can properly infer the type of the Select here
   * because that would come from the value of the options (as children)
   * which is not really doable with the given API.
   */
  OptionValue,
  Multiple extends boolean,
> = SelectBaseProps<OptionValue, Multiple> &
  FormControlProps<
    Multiple extends true
      ? SelectMultiValue<OptionValue>
      : SelectSingleValue<OptionValue>,
    (value: SelectOnChangeValue<OptionValue, Multiple>) => void
  > &
  FormControlStateProps &
  DOMProps &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps;

/**
 * Function to return the item that should be focused after
 * updating all options via onChange in a controlled multi select.
 * @param options - all select options
 * @param focusedOptionValue - the value of the previously focused option
 * @returns option with the same value as the previously focused option or null
 */
function getNewFocusedOption(
  options: OptionOrGroup<BaseOption<unknown>>,
  focusedOptionValue: unknown,
): BaseOption<unknown> | null {
  for (const optionOrGroup of options) {
    if (_isOption(optionOrGroup)) {
      if (optionOrGroup.value === focusedOptionValue) {
        return optionOrGroup;
      }
    } else {
      for (const option of optionOrGroup.options) {
        if (_isOption(option) && option.value === focusedOptionValue) {
          return option;
        }
      }
    }
  }

  return null;
}

/** Select component */
function SelectFwd<OptionValue, Multiple extends boolean = false>(
  props: SelectFormsProps<OptionValue, Multiple>,
  forwardedRef: Ref<FormControlWithOverlayRef>,
) {
  const {
    multiple = false,
    disabled: propsDisabled,
    controlState,
    clearable = multiple,
    name,
    id: propId,
    readOnly: propReadOnly,
    children,
    value: propsValue,
    defaultValue: propsDefaultValue,
    onChange: propsOnChange,
    onFocus,
    onBlur,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    onOpenChange,
    required: propsRequired,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const slots = useSelectSlots(children);

  const stratoLogger = useMemo(
    () =>
      createStratoLogger.bind({
        name,
        id: propId,
        ariaLabel,
        dataTestId,
      })(COMPONENT_NAME),
    [name, propId, ariaLabel, dataTestId],
  );

  const {
    disableFiltering,
    value: filterValueProp,
    defaultValue: filterDefaultValueProp,
    onChange: filterOnChangeProp,
    ...remainingFilterProps
  } = slots.Filter?.props ?? {};

  const {
    width: triggerWidthProp,
    placeholder: triggerPlaceholderProp,
    ...remainingTriggerProps
  } = slots.Trigger?.props ?? {};

  const {
    width: contentWidthProp,
    loading: contentLoadingProp,
    showSelectedOptionsFirst: contentShowSelectedOptionsFirstProp,
    ...remainingContentProps
  } = slots.Content?.props ?? {};

  const {
    optionsOrGroups: options,
    optionsHavePrefix,
    allOptions,
  } = useSelectOptions<OptionValue>(slots.Content, stratoLogger);

  const { value, defaultValue } = useSelectValue<OptionValue, Multiple>({
    value: propsValue,
    defaultValue: propsDefaultValue,
    options,
    multiple: (multiple === true) as Multiple,
  });

  const hiddenSelectRef = useRef<HTMLSelectElement>(null);

  const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);

  const { disabled, required, id, ariaDisabled, readOnly } =
    useSyncedFormControlProps({
      propDisabled: propsDisabled,
      propRequired: propsRequired,
      propId,
      propAriaDisabled: ariaDisabledProp,
      propReadOnly,
    });

  const { onBlur: validationOnBlur } = useFormFieldValidation(hiddenSelectRef, {
    required,
  });

  const menuOpenReducer = useCallback(
    (state: boolean, action: SelectReducerAction) => {
      if (readOnly) {
        return state;
      }

      const newState = reducer(state, action);

      if (state !== newState) {
        // Delay the onOpenChange call to not run into a bad setState().
        queueMicrotask(() => {
          newState === false && !controlState && validationOnBlur();
          onOpenChange?.(newState);
        });
      }

      return newState;
    },
    [readOnly, controlState, validationOnBlur, onOpenChange],
  );

  const [menuOpen, dispatch] = useReducer(menuOpenReducer, false);

  const [isFocusWithin, setIsFocusWithin] = useState<boolean>(false);

  const [filterValue, setFilterValue] = useControllableState({
    value: filterValueProp,
    defaultValue: filterDefaultValueProp,
    onChange: filterOnChangeProp,
  });

  const { state: messagesContextState, dispatch: messagesContextDispatch } =
    useFormFieldMessagesContext();

  // any is needed as the needed type coming from the react-select is not exported
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  const hiddenTrigger = useMemo(
    () => triggerRef?.querySelector('input'),
    [triggerRef],
  );
  const [focusOnOption, setFocusOnOption] = useState<boolean>(true);

  // If the disableFiltering value changes, the filterValue will need to be
  // reset
  useEffect(() => {
    if (
      disableFiltering === true &&
      (filterValue !== undefined || filterValue !== '')
    ) {
      setFilterValue('');
    }
    // We don't want to include filterValue as dependency because when the user types in the input and
    // disableFiltering is true, we don't want to clear the input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableFiltering]);

  const filterConfig = {
    // when a textValue is on the option, the textValue is used;
    // otherwise we try and stringify whatever possible.
    stringify: (option: {
      label: string;
      value: string;
      data: BaseOption<OptionValue>;
    }) => {
      return `${option.label}`;
    },
  };

  const handleChange = (
    option:
      | SelectSingleValue<BaseOption<OptionValue>>
      | SelectMultiValue<BaseOption<OptionValue>>,
  ) => {
    // Send native onChange event for any form control / onChange listeners
    requestAnimationFrame(() => {
      const nativeEvent = new Event('change', {
        bubbles: true,
        cancelable: true,
      });
      hiddenSelectRef?.current?.dispatchEvent(nativeEvent);
    });

    if (multiple === false) {
      setFilterValue(''); // clear the filter when an option is selected in single mode
    }

    // Early exit if the onChange prop is not defined, there is nothing that needs
    // to be done.
    if (!propsOnChange) {
      return;
    }

    // In a single select scenario, it will only emit a single value of the selected option
    if (multiple === false) {
      // If the clicked option is the same as the selected one we can return early.
      if (option === value) {
        return;
      }
      if (option === null) {
        // Ensures that null is returned if no value is selected
        propsOnChange?.(null as SelectOnChangeValue<OptionValue, Multiple>);
      } else {
        propsOnChange?.(
          (option as SelectSingleValue<BaseOption<OptionValue>>)
            ?.value as SelectOnChangeValue<OptionValue, Multiple>,
        );
      }
    }

    // In a multi select scenario, it will map the value of all selected options and report them
    // in the on change.
    if (multiple === true) {
      const selectedOptionValues = (
        option as SelectMultiValue<BaseOption<OptionValue>>
      ).map((individualOption) => individualOption.value);
      propsOnChange?.(
        selectedOptionValues as SelectOnChangeValue<OptionValue, Multiple>,
      );
    }
  };

  const filterRef = useRef<FormControlRef<HTMLDivElement>>(null);
  const clearSelectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuListFunctionRef = useRef<MenuListFunctionRefObject>(null);

  const handleArrowKeyOptionFocus = (key: KeyboardEvent['key']) => {
    if (key !== 'ArrowUp' && key !== 'ArrowDown') {
      return;
    }

    requestAnimationFrame(() => {
      if (!contentRef.current) {
        return;
      }

      const container = contentRef.current;
      const focusedElement = container.querySelector(
        '.strato-select-option--focus',
      );

      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
        });
      } else if (container) {
        if (key === 'ArrowDown') {
          container.scrollTop = 0;
        } else if (menuListFunctionRef.current) {
          menuListFunctionRef.current.scrollToBottom();
        }
      }
    });
  };

  useImperativeHandle(
    forwardedRef,
    (): FormControlWithOverlayRef => ({
      element: containerRef.current,
      triggerRef: triggerRef,
      inputRef: hiddenSelectRef.current,
      focus: () => {
        hiddenTrigger?.focus();
      },
      open: () => {
        // When opened programmatically the hidden input needs to be focused for the virtual focus
        hiddenTrigger?.focus();
        dispatch({
          type: SelectActions.OPEN,
        });
      },
      close: () =>
        dispatch({
          type: SelectActions.CLOSE,
        }),
      validate: () => {
        // The initialization of the hiddenSelectRef is delayed, therefore,
        // we want to check the validity at the end of the render cycle.
        queueMicrotask(() => hiddenSelectRef.current?.reportValidity());
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    }),
    [hiddenTrigger, messagesContextDispatch, triggerRef],
  );

  /**
   * Workaround for the focus to stay on the selected option for the controlled multi select
   * See: https://github.com/JedWatson/react-select/issues/5367
   */
  if (multiple && propsOnChange) {
    const select = selectRef.current;
    if (select?.state.focusedOption) {
      const focusedOptionValue = select.state.focusedOption.value;
      const newFocusedOption = getNewFocusedOption(options, focusedOptionValue);

      // HACK: manipulating the state without setState is evil (but needed here as we are mid-transition)
      select.state.focusedOption = newFocusedOption;
    }
  }

  const [hasFormControlError] = useFormControlState(controlState);
  const showError = controlState
    ? hasFormControlError
    : messagesContextState.showError;

  const contextMemo = useMemo(
    () => ({ state: menuOpen, dispatch }),
    [menuOpen, dispatch],
  );

  const onBlurHandler = (event: FocusEvent<HTMLInputElement, Element>) => {
    if (
      // The relatedTarget is null when the focus leaves the browser and we want to still trigger the onBlur in this case.
      !event.relatedTarget ||
      (event.relatedTarget !== hiddenTrigger &&
        event.relatedTarget !== clearSelectionRef.current &&
        event.relatedTarget !== filterRef.current?.inputRef)
    ) {
      if (!menuOpen && isFocusWithin) {
        setIsFocusWithin(false);
        onBlur?.(event);
      }
    }
  };

  return (
    <SelectMenuContext.Provider value={contextMemo}>
      <div
        ref={containerRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(
          styles.selectWrapper({
            fullWidth:
              triggerWidthProp === 'full' || triggerWidthProp === '100%',
          }),
          consumerClassName,
        )}
        style={consumerStyle}
        {...mergeProps(restProps, behavioralTrackingProps)}
        data-dt-component={COMPONENT_NAME}
      >
        <ReactSelect
          instanceId={id}
          selectInputId={id}
          className={clsx(styles.select, styles.clearReactSelectZIndex, {
            [styles.validAndEnabled]: !disabled && !ariaDisabled && !showError,
          })}
          unstyled
          options={options}
          components={components}
          // TODO: React select does an instance equality check on this one, so we need to figure out how
          // to keep that in sync over a controlled state.
          value={value}
          defaultValue={defaultValue}
          onChange={(value) =>
            // Need to cast this here as React.Select handles the value still as unknown.
            // TODO: Maybe we can go to the JSX generic?
            handleChange(
              value as
                | SelectSingleValue<BaseOption<OptionValue>>
                | SelectMultiValue<BaseOption<OptionValue>>,
            )
          }
          onBlur={onBlurHandler}
          hiddenSelectName={name}
          ref={selectRef}
          pageSize={5}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          aria-disabled={ariaDisabled}
          hiddenSelectRef={hiddenSelectRef}
          // This has to be set to false as we don't want the input directly in the select trigger
          // but handle this ourselves
          isSearchable={false}
          // Multi selection relevant props
          isMulti={multiple ?? false}
          hideSelectedOptions={false}
          closeMenuOnSelect={multiple === false}
          // This has to be set to false as we don't want the clear button directly in the select trigger
          // but handle this ourselves
          isClearable={false}
          isDisabled={disabled}
          isRequired={required}
          triggerWidth={triggerWidthProp}
          contentWidth={contentWidthProp ?? 'trigger'}
          placeholderText={triggerPlaceholderProp}
          // DISCUSS: Figure out if `undefined` is better here or if we should use `null`
          displayValue={slots.DisplayValue ?? undefined}
          prefix={slots.Prefix ?? undefined}
          customTrigger={slots.CustomTrigger ?? undefined}
          // Trigger and Menu (Popper) ref connections
          triggerRef={triggerRef}
          setTriggerRef={setTriggerRef}
          // Reserving space for prefix icons
          optionsHavePrefix={optionsHavePrefix}
          menuIsOpen={menuOpen}
          // Handles Filtering in the current select
          filterOption={(option, inputValue) => {
            if (disableFiltering === true) {
              return true;
            }
            const filterFunction =
              createFilter<BaseOption<OptionValue>>(filterConfig);

            // We need to cast this here, because the filter option prop only infers option as `FilterOptionOption<unknown>`. The `FilterOptionOption` type is not exposed by react-select.
            return filterFunction(
              option as Parameters<typeof filterFunction>[0],
              inputValue,
            );
          }}
          customFilter={slots.Filter ?? undefined}
          inputValue={filterValue}
          onInputChange={(newValue, actionmeta) => {
            if (actionmeta.action === 'input-change') {
              setFilterValue(newValue);
            }
          }}
          emptyState={slots.EmptyState ?? undefined}
          controlState={controlState}
          loading={contentLoadingProp}
          clearable={clearable}
          allOptions={allOptions}
          tabSelectsValue={false}
          focusOnOption={focusOnOption}
          contentRef={contentRef}
          menuListFunctionRef={menuListFunctionRef}
          filterRef={filterRef}
          clearSelectionRef={clearSelectionRef}
          showSelectedOptionsFirst={contentShowSelectedOptionsFirstProp}
          componentProps={{
            trigger: {
              className: slots.Trigger?.props.className,
              style: { ...slots.Trigger?.props.style },
              readOnly: !!readOnly,
              ariaDisabled,
              ...remainingTriggerProps,
            },
            filter: {
              className: slots.Filter?.props.className,
              style: slots.Filter?.props.style,
              ...remainingFilterProps,
            },
            content: {
              className: slots.Content?.props.className,
              style: slots.Content?.props.style,
              ...remainingContentProps,
            },
            emptyState: {
              ...slots.EmptyState?.props,
            },
          }}
          onFocus={(event) => {
            if (!isFocusWithin) {
              onFocus?.(event);
              setIsFocusWithin(true);
            }
          }}
          onKeyDown={(event) => {
            if (!menuOpen) {
              if (event.key === 'Enter' || event.key === ' ') {
                dispatch({
                  type: SelectActions.SELECTFOCUS,
                });
              }

              return true;
            }

            // close the dropdown on Escape and focus the trigger
            if (event.key === 'Escape') {
              dispatch({
                type: SelectActions.SELECTFOCUS,
              });

              hiddenTrigger?.focus();
            }

            if (filterRef.current || clearSelectionRef.current) {
              // focus is on filter
              if (document.activeElement === filterRef.current?.inputRef) {
                // We only want to remove selected values with Backspace when the focus is not on the filter input
                if (
                  event.key === 'Backspace' &&
                  filterRef.current.inputRef?.value === ''
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                // focus back on the trigger on tab if there is no clear selection element or shift tab is pressed
                if (event.key === 'Tab') {
                  if (!clearSelectionRef.current || event.shiftKey) {
                    event.preventDefault();
                    event.stopPropagation();
                    hiddenTrigger?.focus();
                  } else {
                    clearSelectionRef.current && setFocusOnOption(false); // prevent setting the focus on the option if there is a clear selection button when tabbing - the clear button should be the only element focused then
                  }
                }

                // close the overlay when using a single select and selecting an option while searching in the filter
                if (event.key === 'Enter' && !props.multiple) {
                  dispatch({
                    type: SelectActions.SELECTFOCUS,
                  });
                  hiddenTrigger?.focus();
                }

                // do not select any options if the select is in a loading state
                if (event.key === 'Enter' && contentLoadingProp) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                // do not select any options if typing space as the first character of the filter
                if (event.key === ' ' && !filterValue) {
                  setFilterValue(' ');
                  event.preventDefault();
                  event.stopPropagation();
                }

                handleArrowKeyOptionFocus(event.key);

                return true;
              }

              // focus is on the clear selection element
              if (document.activeElement === clearSelectionRef.current) {
                setFocusOnOption(false);
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                }

                // focus the trigger on tab (if it is not a shift tab press to focus back on the filter)
                if (
                  event.key === 'Tab' &&
                  !(event.shiftKey && filterRef.current)
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  hiddenTrigger?.focus();
                  setFocusOnOption(true);
                }

                // focus moves from the clear selection element back to the input
                if (
                  event.key === 'Tab' &&
                  event.shiftKey &&
                  filterRef.current
                ) {
                  setFocusOnOption(true);
                }

                return false;
              }

              try {
                // focus is on hidden input element
                if (
                  document.activeElement &&
                  document.getElementById(document.activeElement.id)
                ) {
                  if (event.key === 'Tab') {
                    event.preventDefault();
                    event.stopPropagation();

                    if (!event.shiftKey) {
                      // set focus on filter if available
                      if (filterRef.current) {
                        filterRef.current.inputRef?.focus();
                        return false;
                      }

                      // set focus on clearSelection if available
                      if (clearSelectionRef.current) {
                        clearSelectionRef.current.focus();
                        setFocusOnOption(false);
                        return false;
                      }
                    } else {
                      // set focus on clearSelection if available
                      if (clearSelectionRef.current) {
                        clearSelectionRef.current.focus();
                        setFocusOnOption(false);
                        return false;
                      }
                      // set focus on filter if available
                      if (filterRef.current) {
                        filterRef.current.inputRef?.focus();
                        return false;
                      }
                    }
                  }

                  handleArrowKeyOptionFocus(event.key);

                  // do not select any options if the select is in a loading state
                  if (contentLoadingProp) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }
              } catch (error) {
                // eslint-disable-next-line no-console
                console.log(
                  'Invalid query selector on the Select trigger',
                  error,
                );
              }
              return false;
            }

            try {
              // close the dropdown when tab is pressed and no filter or clear selection is available
              if (
                document.activeElement &&
                document.getElementById(document.activeElement.id)
              ) {
                if (event.key === 'Tab') {
                  dispatch({
                    type: SelectActions.SELECTFOCUS,
                  });
                } else {
                  handleArrowKeyOptionFocus(event.key);
                }
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log(
                'Invalid query selector on the Select trigger',
                error,
              );
            }

            setFocusOnOption(true);
          }}
        />
      </div>
    </SelectMenuContext.Provider>
  );
}

const SelectForwardRef = forwardRef(SelectFwd);

/**
 * Select component that uses a fallback FormFieldMessages-context if it wasn't wrapped in a FormField.
 */
const SelectRoot =
  withFormFieldMessageContextProviderWithGenerics(SelectForwardRef);

/**
 * The `Select` component allows you to choose one or multiple
 * options from a collapsed dropdown with options.
 * @public
 */
export const Select = Object.assign(SelectRoot, {
  Content: SelectContent,
  Option: SelectOption,
  Group: SelectGroup,
  GroupLabel: SelectGroupLabel,
  Trigger: SelectTrigger,
  DisplayValue: SelectDisplayValue,
  Prefix: SelectPrefix,
  Suffix: SelectSuffix,
  CustomTrigger: SelectCustomTrigger,
  Filter: SelectFilter,
  EmptyState: SelectEmptyState,
});

(Select as typeof Select & { displayName: string }).displayName =
  COMPONENT_NAME;
